import {
  Component,
  inject,
  signal,
  computed,
  effect,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  Renderer2
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { SlideOverPanelService } from './slide-over-panel.service';

@Component({
  selector: 'app-slide-over-panel',
  imports: [NgTemplateOutlet, CdkTrapFocus],
  templateUrl: './slide-over-panel.component.html',
  styleUrl: './slide-over-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SlideOverPanelComponent implements OnDestroy {
  protected readonly panel = inject(SlideOverPanelService);
  private readonly renderer = inject(Renderer2);

  /** Current panel width in pixels */
  readonly panelWidth = signal(560);

  /** Whether the user is dragging the resize handle */
  readonly isResizing = signal(false);

  readonly statusClass = computed(() => {
    const status = this.panel.config().status;
    return status ? `status-${status.type}` : '';
  });

  private resizeCleanup: (() => void)[] = [];

  constructor() {
    effect(() => {
      const width = this.panel.config().initialWidth;
      if (width) {
        this.panelWidth.set(width);
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.panel.isOpen()) {
      this.panel.close();
    }
  }

  onBackdropClick(): void {
    if (!this.isResizing()) {
      this.panel.close();
    }
  }

  // ─── Resize ──────────────────────────────────────────────

  onResizeStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing.set(true);

    const startX = this.clientX(event);
    const startWidth = this.panelWidth();
    const config = this.panel.config();
    const minW = config.minWidth ?? 420;
    const maxW = window.innerWidth * ((config.maxWidthVw ?? 80) / 100);

    const clamp = (x: number) => Math.min(maxW, Math.max(minW, x));

    const onMove = (e: MouseEvent | TouchEvent) => {
      const delta = startX - this.clientX(e);
      this.panelWidth.set(clamp(startWidth + delta));
    };

    const onEnd = () => {
      this.isResizing.set(false);
      this.teardownResize();
    };

    this.resizeCleanup = [
      this.renderer.listen('document', 'mousemove', onMove),
      this.renderer.listen('document', 'touchmove', onMove),
      this.renderer.listen('document', 'mouseup', onEnd),
      this.renderer.listen('document', 'touchend', onEnd)
    ];
  }

  private clientX(e: MouseEvent | TouchEvent): number {
    return e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
  }

  private teardownResize(): void {
    this.resizeCleanup.forEach(fn => fn());
    this.resizeCleanup = [];
  }

  ngOnDestroy(): void {
    this.teardownResize();
  }
}
